/**
 * Tenant (obrador) + User schemas.
 *
 * `tenants` is NOT tenant-scoped (platform-managed); it has no tenant_id. `users` IS
 * tenant-scoped, so its input schema omits tenant_id (injected server-side).
 */

import { z } from 'zod';
import {
  TENANT_STATUSES,
  TENANT_PLANS,
  APP_LANGUAGES,
  TENANT_SECTORS,
  USER_ROLES,
  type UserRole,
} from '../enums/index.js';
import { PIN_ROLES } from '../domain/roleApps.js';
import { isDeviceSecretShape } from '../domain/deviceSecret.js';
import {
  ENROL_CODE_LENGTH,
  ENROL_PREFIX_LENGTH,
  isValidEnrolCode,
  normalizeEnrolCode,
} from '../domain/pinCredentials.js';
import {
  uuid,
  nonEmptyString,
  timestamptz,
  jsonObject,
  softDeleteFields,
  timestampFields,
} from './common.js';

/** Persisted tenant row (read shape). Platform-console managed. */
export const Tenant = z
  .object({
    id: uuid,
    name: nonEmptyString,
    legal_name: nonEmptyString.nullable(),
    tax_id: z.string().trim().min(1).nullable(),
    sector: z.enum(TENANT_SECTORS),
    plan: z.enum(TENANT_PLANS),
    language: z.enum(APP_LANGUAGES),
    status: z.enum(TENANT_STATUSES),
    settings_json: jsonObject,
    admin_email: z.string().trim().email().nullable(),
    suspended_at: timestamptz.nullable(),
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type Tenant = z.infer<typeof Tenant>;

/** Create payload (platform console). No id/timestamps/soft-delete from client. */
export const TenantCreateInput = z
  .object({
    name: nonEmptyString,
    legal_name: nonEmptyString.optional(),
    tax_id: z.string().trim().min(1).optional(),
    sector: z.enum(TENANT_SECTORS).default('generic'),
    plan: z.enum(TENANT_PLANS).default('obrador'),
    language: z.enum(APP_LANGUAGES).default('ca'),
    admin_email: z.string().trim().email().optional(),
    settings_json: jsonObject.optional(),
  })
  .strict();
export type TenantCreateInput = z.infer<typeof TenantCreateInput>;

/**
 * Persisted user row (read shape). Tenant-scoped.
 *
 * `users.pin_hash` is deliberately NOT read: the column is OBSOLETA (OBR-25) — a PIN
 * lives in `user_pins`, a table the API never exposes. Nothing parses a `users` row
 * with this schema (it never listed `auth_user_id` either), so dropping the key leaves
 * `.strict()` consistent with every caller.
 */
export const User = z
  .object({
    id: uuid,
    tenant_id: uuid,
    email: z.string().trim().email().nullable(),
    full_name: nonEmptyString,
    default_shift_id: uuid.nullable(),
    default_section_id: uuid.nullable(),
    cost_per_hour: z.number().finite().nonnegative().nullable(),
    is_active: z.boolean(),
    ...timestampFields,
    ...softDeleteFields,
  })
  .strict();
export type User = z.infer<typeof User>;

/**
 * User create payload. tenant_id is OMITTED — injected from the JWT, never client-set
 * (multitenancy invariant). `.strict()` rejects any attempt to pass tenant_id.
 *
 * There is no `pin` here: a PIN is never typed in, the system generates it
 * (`OperatorCreateInput` → `PinIssueResult`).
 */
export const UserCreateInput = z
  .object({
    email: z.string().trim().email().optional(),
    full_name: nonEmptyString,
    default_shift_id: uuid.optional(),
    default_section_id: uuid.optional(),
    is_active: z.boolean().default(true),
  })
  .strict();
export type UserCreateInput = z.infer<typeof UserCreateInput>;

/**
 * Assign (or clear) a member's production section — the ONLY thing that makes a
 * SECTION-assigned OT reach a device.
 *
 * The planta pull scopes to `user_id = me OR section_id = my default_section_id`
 * (apps/planta/src/db/pull-query.ts), so a member with no section receives ONLY
 * person-assigned OTs; everything planned for a section is invisible to them.
 *
 * `.strict()` keeps this a single-column update (tenant_id is never client-set), and
 * the column is explicitly NULLABLE rather than optional: `null` is the documented
 * "no section" value, not an omission.
 */
export const UserSectionUpdateInput = z.object({ default_section_id: uuid.nullable() }).strict();
export type UserSectionUpdateInput = z.infer<typeof UserSectionUpdateInput>;

/**
 * One obrador the signed-in account can work in, as returned by the
 * `my_memberships()` RPC (multi-obrador). `is_current` marks the ACTIVE tenant —
 * the one every RLS-scoped read/write currently resolves to. The backoffice
 * switcher lists these and calls `switch_active_tenant(p_tenant_id)` to move.
 */
export const TenantMembership = z
  .object({
    tenant_id: uuid,
    tenant_name: nonEmptyString,
    is_current: z.boolean(),
  })
  .strict();
export type TenantMembership = z.infer<typeof TenantMembership>;

// ---------------------------------------------------------------------------
// Operaris amb PIN, visibilitat de menús i enrolament d'aparells (OBR-25)
//
// The `*Input` bodies are what the backoffice / planta send to the edge functions
// (`tenant-pin-manage`, `set_user_menu_access`, `pin-login`, `device-enrol`). None of
// them carries `tenant_id` or an actor id — the function resolves both from the
// caller's JWT — and `.strict()` rejects a stray one. The results are read shapes: they
// strip unknown keys (zod's default) so an old client survives a newer server.
// ---------------------------------------------------------------------------

/**
 * A PIN as it travels: 7 characters of the 32-symbol alphabet (no 0, O, 1, I), upper
 * case, no separator. The SOURCE OF TRUTH is `domain/pin.ts` (`PIN_ALPHABET`,
 * `PIN_LENGTH`); the literal is repeated here so this module does not depend on it.
 */
const pinFormat = z.string().regex(/^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{7}$/, 'invalid_pin_format');

/**
 * The shape of a menu key — the same pattern as the CHECK on `user_menu_access.menu_key`.
 * Whether the key EXISTS is the registry's job (`validateHiddenKeys`, domain/menuAccess.ts).
 */
const menuKeyFormat = z.string().regex(/^(bo|pl)(\.[a-z_]+)+$/, 'invalid_menu_key');

const hasNoDuplicates = (values: readonly string[]): boolean =>
  new Set(values).size === values.length;

const isPinRole = (role: UserRole): boolean => PIN_ROLES.includes(role);

/** A name or a reason is one line: no tabs, no line breaks, no other control characters. */
const hasControlCharacter = (text: string): boolean =>
  [...text].some((symbol) => {
    const code = symbol.charCodeAt(0);
    return code < 32 || code === 127;
  });

/** A lone UTF-16 surrogate (half of a pair) is not text: the database would reject it for good. */
const LONE_SURROGATE = /[\ud800-\udbff](?![\udc00-\udfff])|(?<![\ud800-\udbff])[\udc00-\udfff]/;
const isWellFormedText = (text: string): boolean => !LONE_SURROGATE.test(text);

/**
 * Create a PIN operator (`tenant-pin-manage` · `create`). `requestId` makes the call
 * idempotent: a retry with the same id answers `409 already_issued` instead of issuing a
 * second PIN. `label` is optional (the default is «Operari N»); `linkedUserId` only ends
 * up in the audit log. Roles are limited to the ones that open planta — never admin.
 */
export const OperatorCreateInput = z
  .object({
    requestId: uuid,
    label: z
      .string()
      .trim()
      .min(1)
      .max(120)
      .refine((text) => !hasControlCharacter(text), 'invalid_label')
      .refine(isWellFormedText, 'invalid_label')
      .optional(),
    roles: z
      .array(z.enum(USER_ROLES).refine(isPinRole, 'role_not_allowed'))
      .min(1)
      .refine(hasNoDuplicates, 'duplicate_roles'),
    defaultSectionId: uuid.optional(),
    linkedUserId: uuid.optional(),
  })
  .strict();
export type OperatorCreateInput = z.infer<typeof OperatorCreateInput>;

/**
 * What issuing a PIN (create, regenerate, reactivate, convert) answers. The PIN is never stored in the
 * clear: it is kept encrypted, and only `tenant-pin-manage` `list` (administration) reads it back
 * (`PinListResult`). `list_user_pins` never returns it.
 */
export const PinIssueResult = z.object({
  userId: uuid,
  pin: pinFormat,
  /** Només a `create`: el nom definitiu (el per defecte «Operari N» si l'administrador no n'ha escrit cap). */
  fullName: nonEmptyString.optional(),
});
export type PinIssueResult = z.infer<typeof PinIssueResult>;

/**
 * Replace the set of HIDDEN menu options of one person (`set_user_menu_access`). The
 * list is the whole set, not a delta; an empty list restores everything.
 */
export const MenuAccessInput = z
  .object({
    userId: uuid,
    hiddenKeys: z.array(menuKeyFormat).refine(hasNoDuplicates, 'duplicate_menu_keys'),
  })
  .strict();
export type MenuAccessInput = z.infer<typeof MenuAccessInput>;

/**
 * The device secret as `device-enrol` hands it out (`domain/deviceSecret.ts`): 128 lowercase hexadecimal
 * characters = device id ‖ 256 random bits ‖ a 128-bit tag. The tag is checked before anything else, so a
 * secret of any other shape never reaches the database.
 */
const deviceSecretFormat = z.string().refine(isDeviceSecretShape, 'invalid_device_secret');

/**
 * Planta signs in with a PIN (`pin-login`, public). `deviceSecret` is the secret the device
 * got when it was enrolled; without it the function does not even look at the PIN.
 * `installId` is a convenience label for the device, not a security control.
 */
export const PinLoginInput = z
  .object({
    pin: pinFormat,
    installId: uuid,
    /** Absent on the device-less login (planta since 16/10/2026); present for a tablet enrolled the old way. */
    deviceSecret: deviceSecretFormat.optional(),
  })
  .strict();
export type PinLoginInput = z.infer<typeof PinLoginInput>;

/**
 * What a good PIN login answers — the same shape planta already gets from the email
 * sign-in (both tokens in the body, since the refresh cookie is not usable on a device),
 * plus who and where, so the lock screen can tell the parked operator from another one.
 */
export const PinLoginResponse = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  userId: uuid,
  tenantId: uuid,
});
export type PinLoginResponse = z.infer<typeof PinLoginResponse>;

/**
 * Enrol a device (`device-enrol`, public): the one-time code the administrator got from
 * `tenant-device-manage` plus the device's own `installId`. The code is what a person types:
 * it is normalised (no spaces or dashes, upper case) and must then be 7 symbols of the PIN
 * alphabet (a 3-symbol prefix that finds the live code + a 4-symbol secret).
 */
export const DeviceEnrolInput = z
  .object({
    code: z.string().transform(normalizeEnrolCode).refine(isValidEnrolCode, 'invalid_enrol_code'),
    installId: uuid,
  })
  .strict();
export type DeviceEnrolInput = z.infer<typeof DeviceEnrolInput>;

/** What a good enrolment answers: the device row and the secret the tablet keeps in its secure store. */
export const DeviceEnrolResponse = z.object({
  deviceId: uuid,
  deviceSecret: deviceSecretFormat,
});
export type DeviceEnrolResponse = z.infer<typeof DeviceEnrolResponse>;

// ---------------------------------------------------------------------------
// The two management functions (`tenant-pin-manage`, `tenant-device-manage`): JWT of an
// administrator. Like every body above, none of them carries the obrador or an actor id.
// ---------------------------------------------------------------------------

/** The longest written reason (regenerate, revoke) the functions accept; the BackOffice caps its text box with it. */
export const PIN_REASON_MAX_LENGTH = 500;

/** A written reason (regenerate, revoke): one line, 1–500 characters. The audit log keeps it. */
const reasonText = z
  .string()
  .trim()
  .min(1)
  .max(PIN_REASON_MAX_LENGTH)
  .refine((text) => !hasControlCharacter(text), 'invalid_reason')
  .refine(isWellFormedText, 'invalid_reason');

/**
 * Everything `tenant-pin-manage` accepts. `requestId` is on every action that issues a PIN: a
 * double click or a retry with the same id answers `409 already_issued` instead of issuing a second
 * PIN, so the PIN the first answer carried is the one that works.
 */
export const PinManageInput = z.discriminatedUnion('action', [
  OperatorCreateInput.extend({ action: z.literal('create') }),
  z
    .object({ action: z.literal('regenerate'), requestId: uuid, userId: uuid, reason: reasonText })
    .strict(),
  z.object({ action: z.literal('revoke'), userId: uuid, reason: reasonText }).strict(),
  z.object({ action: z.literal('reactivate'), requestId: uuid, userId: uuid }).strict(),
  z.object({ action: z.literal('convert'), requestId: uuid, userId: uuid }).strict(),
  z.object({ action: z.literal('list') }).strict(),
]);
export type PinManageInput = z.infer<typeof PinManageInput>;

/**
 * `list` answers the live PINs of the obrador in the clear, for Configuració · Operaris: the PIN is kept
 * encrypted (the key lives in the edge function, not in the database) and only an administrator with the
 * Operaris menu can read it back. A person whose PIN predates this (or whose encrypted copy could not be
 * saved) is simply absent from the list.
 */
export const PinListResult = z.object({
  pins: z.array(z.object({ userId: uuid, pin: pinFormat })),
});
export type PinListResult = z.infer<typeof PinListResult>;

/** `revoke` answers only who: a retired PIN has nothing to show. */
export const PinRevokeResult = z.object({ userId: uuid });
export type PinRevokeResult = z.infer<typeof PinRevokeResult>;

/** Everything `tenant-device-manage` accepts. `ttlMinutes` is the life of a one-time code (default 60, max 24 h). */
export const DeviceManageInput = z.discriminatedUnion('action', [
  z
    .object({
      action: z.literal('create_code'),
      deviceId: uuid,
      ttlMinutes: z.number().int().min(1).max(1440).optional(),
    })
    .strict(),
  z.object({ action: z.literal('revoke'), deviceId: uuid }).strict(),
  z.object({ action: z.literal('unlock_device'), deviceId: uuid }).strict(),
]);
export type DeviceManageInput = z.infer<typeof DeviceManageInput>;

/**
 * `create_code` answers the one-time code (shown ONCE: only its prefix and its hash are stored), its
 * prefix (also listed in the backoffice, to tell codes apart) and when it expires.
 */
export const DeviceCodeResult = z.object({
  code: z.string().length(ENROL_CODE_LENGTH).refine(isValidEnrolCode, 'invalid_enrol_code'),
  prefix: z.string().length(ENROL_PREFIX_LENGTH),
  expiresAt: timestamptz,
});
export type DeviceCodeResult = z.infer<typeof DeviceCodeResult>;

/** `revoke`: was there an active credential to revoke? (A device that was never enrolled answers `false`.) */
export const DeviceRevokeResult = z.object({ deviceId: uuid, revoked: z.boolean() });
export type DeviceRevokeResult = z.infer<typeof DeviceRevokeResult>;

/** `unlock_device`: was the device actually locked? */
export const DeviceUnlockResult = z.object({ deviceId: uuid, unlocked: z.boolean() });
export type DeviceUnlockResult = z.infer<typeof DeviceUnlockResult>;

// ---------------------------------------------------------------------------
// The Operaris list (`list_user_pins`)
// ---------------------------------------------------------------------------

/** The three states of a person on the Operaris list (`list_user_pins.status`). */
export const PIN_STATUSES = ['active', 'pending', 'deleted'] as const;
export type PinStatus = (typeof PIN_STATUSES)[number];

/**
 * One row of `list_user_pins()`: a person with a PIN, as the Operaris tab lists it. `active` = a live PIN
 * with its account; `pending` = the person exists but holds no live PIN (an issue that stopped half way, or
 * a retired PIN on a live person); `deleted` = donat de baixa. It never carries a PIN, a lookup or an
 * account id — the function does not return them. `roles` is `NULL` in SQL for a person with none.
 */
export const PinListRow = z.object({
  user_id: uuid,
  full_name: z.string(),
  roles: z
    .array(z.string())
    .nullable()
    .transform((roles) => roles ?? []),
  default_section_id: uuid.nullable(),
  is_active: z.boolean(),
  status: z.enum(PIN_STATUSES),
  issued_at: timestamptz.nullable(),
  last_used_at: timestamptz.nullable(),
  last_used_device: z.string().nullable(),
  deleted_at: timestamptz.nullable(),
  deletion_reason: z.string().nullable(),
});
export type PinListRow = z.infer<typeof PinListRow>;
